// One column: its heading, its cards, and the way to start a new one in it.
//
// THE COLUMN IS THE DROP TARGET, not the list inside it - a column with nothing
// in it is exactly the one you most need to be able to drop on, and a list with
// no rows has no height to aim at.
//
// EVERY COLUMN THAT IS NOT THE COMPLETING ONE CARRIES ITS OWN COMPOSER. That is
// what keeps this screen out of the trap the client has fallen into five times
// (web/src/README.md): the empty board's line can point at a control that is on
// screen at every width, because there is one at the foot of every column.
// Complete has none on purpose - a card gets there by being finished, and a
// composer that created something already done would be a second way to write a
// completion, which is the one thing this feature may not have.

import { useEffect, useRef, useState } from 'react';
import type { BoardColumn, IsoDay } from '../api/client.ts';
import { Icon } from '../day/Icon.tsx';
import { BoardCardView } from './BoardCardView.tsx';
import { neighbour } from './board-model.ts';
import type { BoardResponse } from '../api/client.ts';

interface Props {
  board: BoardResponse;
  column: BoardColumn;
  today: IsoDay;
  busy: ReadonlySet<string>;
  adding: boolean;
  refreshing: boolean;
  loadingOlder: boolean;
  completePagingReady: boolean;
  draggingId: string | null;
  onLoadOlder: (cursor: string) => void;
  onMove: (cardId: string, toKey: string) => void;
  onArchive: (cardId: string) => void;
  onAdd: (title: string) => Promise<boolean>;
  onDragStart: (cardId: string) => void;
  onDragEnd: () => void;
}

export function BoardColumnView({
  board,
  column,
  today,
  busy,
  adding,
  refreshing,
  loadingOlder,
  completePagingReady,
  draggingId,
  onLoadOlder,
  onMove,
  onArchive,
  onAdd,
  onDragStart,
  onDragEnd,
}: Props) {
  const [over, setOver] = useState(false);
  const [completeOpen, setCompleteOpen] = useState(false);
  const shownRef = useRef<HTMLParagraphElement>(null);
  const loadOlderHadFocus = useRef(false);
  const wasLoadingOlder = useRef(false);
  const left = neighbour(board, column.key, -1);
  const right = neighbour(board, column.key, 1);
  const nextCursor = column.nextCursor;
  const olderUnavailable = refreshing || loadingOlder || !completePagingReady;

  useEffect(() => {
    const settled = wasLoadingOlder.current && !loadingOlder;
    wasLoadingOlder.current = loadingOlder;
    if (!settled) return;
    if (loadOlderHadFocus.current && nextCursor === null) shownRef.current?.focus();
    loadOlderHadFocus.current = false;
  }, [loadingOlder, nextCursor]);

  return (
    <section
      className={['bcol', column.completes ? 'bcol-complete' : '', over ? 'over' : '']
        .filter(Boolean)
        .join(' ')}
      // Named with its TOTAL, not only the bounded Complete page, so moving
      // between columns with a screen reader says how many each one really has.
      aria-label={`${column.label}, ${column.total} ${column.total === 1 ? 'card' : 'cards'}`}
      onDragOver={(event) => {
        // Without this the browser refuses the drop: preventing the default on
        // dragover IS what marks an element as a drop target.
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
        setOver(true);
      }}
      onDragLeave={(event) => {
        // Only when the pointer has actually left this column: dragging across a
        // card inside it fires dragleave for the card, and clearing on that
        // makes the highlight flicker under the finger.
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOver(false);
      }}
      onDrop={(event) => {
        event.preventDefault();
        setOver(false);
        // Read back off the transfer rather than from a variable: a second drag
        // starting before the first was dropped would have overwritten one.
        const cardId = event.dataTransfer.getData('text/plain');
        if (cardId !== '') onMove(cardId, column.key);
      }}
    >
      <header className="bcol-head">
        <h2>{column.label}</h2>
        <span className="bcol-n tnum">{column.total}</span>
      </header>

      {column.completes ? (
        column.total === 0 ? (
          <p className="bcol-none">
            Finished cards land here. Drag one in, or move it across with the arrow on it.
          </p>
        ) : (
          <>
            <button
              className="bcomplete-toggle"
              type="button"
              aria-expanded={completeOpen}
              aria-controls={`complete-cards-${column.key}`}
              onClick={() => setCompleteOpen((open) => !open)}
            >
              {completeOpen ? 'Hide completed cards' : 'Show completed cards'}
            </button>
            {completeOpen && (
              <div className="bcomplete" id={`complete-cards-${column.key}`}>
                <ul className="bcol-cards bcomplete-cards">
                  {column.cards.map((card) => (
                    <BoardCardView
                      key={card.id}
                      card={card}
                      column={column}
                      left={left}
                      right={right}
                      today={today}
                      busy={busy.has(card.id)}
                      dragging={draggingId === card.id}
                      onMove={(toKey) => onMove(card.id, toKey)}
                      onArchive={() => onArchive(card.id)}
                      onDragStart={() => onDragStart(card.id)}
                      onDragEnd={onDragEnd}
                    />
                  ))}
                </ul>
                <p className="bcomplete-shown" ref={shownRef} tabIndex={-1} aria-live="polite">
                  Showing {column.cards.length} of {column.total}
                </p>
                {nextCursor !== null && (
                  <button
                    className="btn bcomplete-more"
                    type="button"
                    aria-disabled={olderUnavailable}
                    onClick={(event) => {
                      if (olderUnavailable) return;
                      loadOlderHadFocus.current = document.activeElement === event.currentTarget;
                      onLoadOlder(nextCursor);
                    }}
                  >
                    {loadingOlder ? 'Loading older…' : 'Load older'}
                  </button>
                )}
              </div>
            )}
          </>
        )
      ) : (
        <>
          <ul className="bcol-cards">
            {column.cards.map((card) => (
              <BoardCardView
                key={card.id}
                card={card}
                column={column}
                left={left}
                right={right}
                today={today}
                busy={busy.has(card.id)}
                dragging={draggingId === card.id}
                onMove={(toKey) => onMove(card.id, toKey)}
                onArchive={() => onArchive(card.id)}
                onDragStart={() => onDragStart(card.id)}
                onDragEnd={onDragEnd}
              />
            ))}
          </ul>
          {column.cards.length === 0 && <p className="bcol-none">Nothing in {column.label}.</p>}
          <Composer column={column} adding={adding} onAdd={onAdd} />
        </>
      )}
    </section>
  );
}

/**
 * The way a card is started, at the foot of the column it will open in.
 *
 * Closed until it is pressed, because four open forms down a board is four
 * blinking cursors and no board. The input is `autoFocus` on open, which is
 * where the press was going anyway.
 *
 * IT CLOSES ON THE ANSWER, NOT ON THE PRESS. `onAdd` resolves true only once the
 * card is on the server, and until then the form stays up with what was typed
 * still in it - so a create that did not land is pressed again rather than
 * retyped from memory, and `adding` has a form to say "Adding…" on.
 *
 * WHICH MEANS THE FORM IS LOCKED WHILE THE WRITE IS IN FLIGHT, all three of its
 * controls and not only the button. A title typed after the press is a word the
 * create has already gone without, and clearing it on success would throw it
 * away without ever saying so; and a way out pressed after the press closes a
 * form over a card that arrives anyway, because nothing in this client can
 * recall a request. The input is `readOnly` rather than `disabled` DELIBERATELY:
 * a disabled input drops focus, and the retry after a failure has to be one
 * press with the caret still in the words.
 *
 * AND FOCUS IS PUT SOMEWHERE ON THE WAY OUT OF EVERY ONE OF THOSE STATES, which
 * `readOnly` alone does not buy: pressing **Add** with a pointer moves focus to
 * that button and the same press disables it, so the browser drops focus to the
 * document body. A form still up with a title in it and no caret in it is not a
 * one-press retry, and a form that has just closed leaves a keyboard adding its
 * next card from the top of the page. So a failed create returns focus to the
 * input, and a form that closes - added, cancelled or escaped - hands it to the
 * **Add a card** button that replaces it.
 *
 * IT PICKS FOCUS UP, IT NEVER TAKES IT BACK. Only this column's composer locks
 * while its write is in flight, so the person can open another column's and
 * start typing - and a write settling behind them must not pull the caret out
 * of the words they are in the middle of, which would lose every keystroke
 * after it to a button. So the effect moves focus only when focus has NO owner
 * (the body, because the control holding it has just gone) or is still inside
 * this composer; anywhere else, the owed focus is dropped.
 */
function Composer({
  column,
  adding,
  onAdd,
}: {
  column: BoardColumn;
  adding: boolean;
  onAdd: (title: string) => Promise<boolean>;
}) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  // Where focus is owed once this render has happened, since the control it
  // belongs on is either not mounted yet or about to be disabled under it.
  const [owed, setOwed] = useState<'input' | 'opener' | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const openerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (owed === null) return;
    setOwed(null);
    const active = document.activeElement;
    const ownerless = active === null || active === document.body;
    if (!ownerless && formRef.current?.contains(active) !== true) return;
    (owed === 'input' ? inputRef.current : openerRef.current)?.focus();
  }, [owed]);

  const close = () => {
    setOpen(false);
    setTitle('');
    setOwed('opener');
  };

  if (!open) {
    return (
      <button className="bcol-add" ref={openerRef} onClick={() => setOpen(true)}>
        <Icon name="plus" />
        <span>Add a card</span>
      </button>
    );
  }

  return (
    <form
      className="bcol-form"
      ref={formRef}
      onSubmit={(event) => {
        event.preventDefault();
        // Enter reaches this while the button is disabled, so the guard is here
        // rather than only on the button: a second press mid-write would make a
        // second card out of one title.
        if (adding || title.trim() === '') return;
        void onAdd(title).then((added) => {
          if (added) close();
          else setOwed('input');
        });
      }}
    >
      <input
        type="text"
        autoFocus
        ref={inputRef}
        value={title}
        readOnly={adding}
        maxLength={120}
        aria-label={`New card in ${column.label}`}
        placeholder="Renew the passport"
        onChange={(event) => setTitle(event.target.value)}
        onKeyDown={(event) => {
          // Escape closes the composer, the way it dismisses a sheet - the same
          // way out from the same key, so nothing here has to be learned. It is
          // Cancel by another key, so it is shut for the same reason Cancel is
          // while a card is on its way.
          if (event.key === 'Escape' && !adding) close();
        }}
      />
      <div className="bcol-formfoot">
        <button className="btn primary" type="submit" disabled={title.trim() === '' || adding}>
          {adding ? 'Adding…' : 'Add'}
        </button>
        <button className="btn" type="button" disabled={adding} onClick={close}>
          Cancel
        </button>
      </div>
    </form>
  );
}
